import React, { useEffect, useState } from "react";
import "./App.css";
import "./article.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";

import { AuthProvider } from "./AuthContext";
import { CartProvider } from "./CartContext";
import { Header, Footer, CartDrawer, PageTransition } from "./components/Layout";
import { api } from "./api";

import Home from "./pages/Home";
import Shop from "./pages/Shop";
import ProductDetail from "./pages/ProductDetail";
import Checkout from "./pages/Checkout";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Account from "./pages/Account";
import About from "./pages/About";
import Learn from "./pages/Learn";
import ArticleDetail from "./pages/ArticleDetail";
import ConsultDoctor from "./pages/ConsultDoctor";
import TheBoard from "./pages/TheBoard";
import ShareToWin from "./pages/ShareToWin";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import LegalPage from "./pages/LegalPage";
import AdminLogin from "./pages/AdminLogin";
import Admin from "./pages/Admin";
import Chatbot from "./components/Chatbot";
import AuthCallback from "./components/AuthCallback";
import Seo from "./components/Seo";

const SEO_MAP = {
    "/": { title: null, description: null },
    "/shop": { title: "Shop Wellness Rituals", description: "Browse Ayurvedic supplements, oils and daily rituals for hormones, periods and hair — kits and standalone products." },
    "/learn": { title: "Learn — Wellness Journal", description: "Doctor-reviewed articles on hormones, cycles, hair health and everyday women's wellness." },
    "/consult-doctor": { title: "Consult a Doctor", description: "Private 30-minute video consultations with board-reviewed women's health specialists. Request your slot." },
    "/the-board": { title: "The Board — Our Core Team", description: "Meet the practitioners and leaders who review every UCare formulation." },
    "/share-to-win": { title: "Share & Win Coupons", description: "Share your UCare ritual on social media and claim exclusive discount coupons." },
    "/about": { title: "About Us", description: "Quiet luxury, proven science and gentle care — the story behind UCare Essentials." },
    "/login": { title: "Login" },
    "/register": { title: "Create Account" },
    "/account": { title: "My Account" },
    "/checkout": { title: "Checkout" },
    "/forgot-password": { title: "Forgot Password" },
    "/reset-password": { title: "Reset Password" },
    "/privacy": { title: "Privacy Policy" },
    "/terms": { title: "Terms & Conditions" },
    "/privacy-ethics": { title: "Privacy & Ethics" },
    "/medical-disclaimer": { title: "Medical Disclaimer" },
    "/faq": { title: "Certifications & FAQ" },
};

function RouteSeo() {
    const { pathname } = useLocation();
    const cfg = SEO_MAP[pathname] || {};
    // key forces the effect to re-run on every navigation; dynamic pages
    // (product/article) then override with their own richer <Seo/>.
    return <Seo key={pathname} title={cfg.title} description={cfg.description} />;
}

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);
    return null;
}

function StoreShell({ children }) {
    const [settings, setSettings] = useState(null);
    useEffect(() => {
        api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    }, []);
    return (
        <>
            <Header storeName={settings?.store_name} announcement={settings?.announcement} logoUrl={settings?.logo_url} logoSize={settings?.logo_size} />
            {children}
            <Footer settings={settings} />
            <CartDrawer settings={settings} />
            <Chatbot />
        </>
    );
}

function AppRoutes() {
    const location = useLocation();
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Emergent Auth returns to {origin}/account#session_id=... — must be caught BEFORE normal routing.
    if (location.hash?.includes("session_id=")) return <AuthCallback />;
    return (
        <>
            <ScrollToTop />
            <RouteSeo />
            <Routes>
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<Admin />} />
                <Route
                    path="/*"
                    element={
                        <StoreShell>
                            <PageTransition>
                                <Routes>
                                    <Route path="/" element={<Home />} />
                                    <Route path="/shop" element={<Shop />} />
                                    <Route path="/product/:id" element={<ProductDetail />} />
                                    <Route path="/checkout" element={<Checkout />} />
                                    <Route path="/login" element={<Login />} />
                                    <Route path="/register" element={<Register />} />
                                    <Route path="/account" element={<Account />} />
                                    <Route path="/about" element={<About />} />
                                    <Route path="/learn" element={<Learn />} />
                                    <Route path="/learn/:id" element={<ArticleDetail />} />
                                    <Route path="/consult-doctor" element={<ConsultDoctor />} />
                                    <Route path="/the-board" element={<TheBoard />} />
                                    <Route path="/share-to-win" element={<ShareToWin />} />
                                    <Route path="/forgot-password" element={<ForgotPassword />} />
                                    <Route path="/reset-password" element={<ResetPassword />} />
                                    <Route path="/privacy" element={<LegalPage slug="privacy-policy" />} />
                                    <Route path="/terms" element={<LegalPage slug="terms-conditions" />} />
                                    <Route path="/privacy-ethics" element={<LegalPage slug="privacy-ethics" />} />
                                    <Route path="/medical-disclaimer" element={<LegalPage slug="medical-disclaimer" />} />
                                    <Route path="/faq" element={<LegalPage slug="certifications-faq" />} />
                                </Routes>
                            </PageTransition>
                        </StoreShell>
                    }
                />
            </Routes>
        </>
    );
}

export default function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <CartProvider>
                    <AppRoutes />
                </CartProvider>
            </AuthProvider>
        </BrowserRouter>
    );
}
